import * as THREE from "three";
import { InstanceBatch } from "../../core/batches/instance_batch.js";
import { ResourceManager } from "../../../resource_loader/resource_manager.js";
import { ResourceID } from "../../../resource_loader/resource_id.js";
import { Vfx3DGlobalAtlas } from "../../core/textures/vfx_3d_global_atlas.js";
import { AttributeSize } from "../../core/types/core_types.js";

export const ImpactSmokeLayout = {
  aPosStartTime: AttributeSize.Vec4,     // xyz: start_pos, w: start_time
  aVelDuration: AttributeSize.Vec4,      // xyz: velocity, w: duration
  aColor: AttributeSize.Vec4,            // r, g, b, alpha
  aScaleRot: AttributeSize.Vec4,         // x: start_scale, y: end_scale, z: initial_angle, w: rotation_speed
} as const;

export class VfxImpactSmokeBatch extends InstanceBatch<typeof ImpactSmokeLayout>
{
  private static readonly MAX_INSTANCES             : number = 60;
  private static readonly NOISE_SCALE               : number = 1;
  private static readonly DISSOLVE_DIRECTION_WEIGHT : number = 0.4;
  private static readonly DISSOLVE_SOFTNESS         : number = 0.3;
  private static readonly DECELERATION_POWER        : number = 2.5;
  private static readonly RENDER_ORDER              : number = 21;

  private static cached_geometry : THREE.BufferGeometry | null = null;

  constructor()
  {
    const base_geometry = VfxImpactSmokeBatch.getSmokeGeometry();

    let atlas_texture: THREE.Texture | null = null;
    const noise_uv = new THREE.Vector4( 0, 0, 1, 1 );
    try
    {
      atlas_texture = Vfx3DGlobalAtlas.getTexture();
      const uv = Vfx3DGlobalAtlas.getUVRect( ResourceID.CloudNoiseTexture );
      noise_uv.set( uv.min_x, uv.min_y, uv.max_x, uv.max_y );
    }
    catch ( _e )
    {
      // Atlas will be populated lazily in spawnParticle if called before init
    }

    const material = new THREE.ShaderMaterial( {
      uniforms: {
        uNoiseTexture: { value: atlas_texture },
        uNoiseUv: { value: noise_uv },
        uNoiseScale: { value: VfxImpactSmokeBatch.NOISE_SCALE },
        uDirectionWeight: { value: VfxImpactSmokeBatch.DISSOLVE_DIRECTION_WEIGHT },
        uSoftness: { value: VfxImpactSmokeBatch.DISSOLVE_SOFTNESS },
        uDecelerationPower: { value: VfxImpactSmokeBatch.DECELERATION_POWER },
        uTime: { value: 0.0 },
      },
      vertexShader: `
        uniform float uDecelerationPower;
        uniform float uSoftness;
        uniform float uTime;

        attribute vec4 aPosStartTime;
        attribute vec4 aVelDuration;
        attribute vec4 aColor;
        attribute vec4 aScaleRot;

        varying vec2 vNoiseUv;
        varying vec2 vRotatedOffset;
        varying vec4 vColor;
        varying vec2 vThresholdRange;
        varying float vFadeIn;
        varying vec2 vFlightDir2D;

        void main() {
          vNoiseUv = position.xy + vec2( 0.5 );
          
          float start_time = aPosStartTime.w;
          float duration = aVelDuration.w;
          float age = max( 0.0, uTime - start_time );
          
          float progress = clamp( age / duration, 0.0, 1.0 );
          vColor = aColor;

          float threshold = mix( -uSoftness, 1.0 + uSoftness, progress );
          vThresholdRange = vec2( threshold, threshold + uSoftness );
          vFadeIn = clamp( progress / 0.08, 0.0, 1.0 );
          
          float ease_dist = 1.0 - pow( 1.0 - progress, uDecelerationPower );
          vec3 computed_pos = aPosStartTime.xyz + aVelDuration.xyz * duration * ease_dist;
          
          float current_scale = mix( aScaleRot.x, aScaleRot.y, progress );
          
          float angle = aScaleRot.z + aScaleRot.w * duration * ease_dist;
          float cos_a = cos( angle );
          float sin_a = sin( angle );
          
          vRotatedOffset = vec2(
            position.x * cos_a - position.y * sin_a,
            position.x * sin_a + position.y * cos_a
          );
          
          vec3 rotated_local = vec3( vRotatedOffset * current_scale, position.z );
          
          vec4 mv_position = modelViewMatrix * vec4( computed_pos, 1.0 );
          mv_position.xyz += rotated_local;
          gl_Position = projectionMatrix * mv_position;
          
          vec3 view_vel = ( modelViewMatrix * vec4( aVelDuration.xyz, 0.0 ) ).xyz;
          vFlightDir2D = length( view_vel.xy ) > 0.001 ? normalize( view_vel.xy ) : vec2( 0.0, 1.0 );
        }
      `,
      fragmentShader: `
        uniform sampler2D uNoiseTexture;
        uniform vec4 uNoiseUv;
        uniform float uNoiseScale;
        uniform float uDirectionWeight;

        varying vec2 vNoiseUv;
        varying vec2 vRotatedOffset;
        varying vec4 vColor;
        varying vec2 vThresholdRange;
        varying float vFadeIn;
        varying vec2 vFlightDir2D;

        void main() {
          float dist_along_flight = dot( vRotatedOffset, vFlightDir2D );
          float spatial_gradient = clamp( dist_along_flight + 0.5, 0.0, 1.0 );
          
          vec2 atlas_noise_uv = mix( uNoiseUv.xy, uNoiseUv.zw, fract( vNoiseUv * uNoiseScale ) );
          float noise_val = texture2D( uNoiseTexture, atlas_noise_uv ).r;
          float metric = mix( noise_val, spatial_gradient, uDirectionWeight );
          
          float dissolve_alpha = smoothstep( vThresholdRange.x, vThresholdRange.y, metric );
          
          vec4 final_color = vColor;
          final_color.a *= vFadeIn * dissolve_alpha;
          
          gl_FragColor = final_color;
        }
      `,
      transparent: true,
      depthWrite: false,
      depthTest: true,
      blending: THREE.NormalBlending,
      side: THREE.FrontSide,
    } );

    super( { max_instances: VfxImpactSmokeBatch.MAX_INSTANCES, layout: ImpactSmokeLayout }, base_geometry, material );
    this.instanced_mesh.renderOrder = VfxImpactSmokeBatch.RENDER_ORDER;
  }

  private static getSmokeGeometry(): THREE.BufferGeometry
  {
    if ( VfxImpactSmokeBatch.cached_geometry )
      return VfxImpactSmokeBatch.cached_geometry;

    const model = ResourceManager.getModel( ResourceID.Particle2Model );
    let loaded_geom: THREE.BufferGeometry | null = null;

    if ( model )
    {
      model.traverse( child =>
      {
        if ( child instanceof THREE.Mesh && !loaded_geom )
          loaded_geom = child.geometry.clone();
      } );
    }

    const final_geom = loaded_geom ?? new THREE.PlaneGeometry( 1.0, 1.0 );
    VfxImpactSmokeBatch.cached_geometry = final_geom;
    return final_geom;
  }

  public spawnParticle(
    start_pos: THREE.Vector3,
    velocity: THREE.Vector3,
    start_scale: number,
    end_scale: number,
    color: THREE.Color,
    alpha: number,
    initial_angle: number,
    rotation_speed: number,
    start_time: number,
    duration: number
  ): number
  {
    const mat = this.material as THREE.ShaderMaterial;
    if ( !mat.uniforms.uNoiseTexture.value )
    {
      try
      {
        mat.uniforms.uNoiseTexture.value = Vfx3DGlobalAtlas.getTexture();
        const uv = Vfx3DGlobalAtlas.getUVRect( ResourceID.CloudNoiseTexture );
        mat.uniforms.uNoiseUv.value.set( uv.min_x, uv.min_y, uv.max_x, uv.max_y );
      }
      catch ( _e )
      {
        // Atlas not yet ready
      }
    }

    const idx = this.allocateInstanceIndex( start_time, duration );
    if ( idx === -1 )
      return -1;

    const pos_start_attr    = this.geometry.getAttribute( "aPosStartTime" ).array as Float32Array;
    const vel_duration_attr = this.geometry.getAttribute( "aVelDuration" ).array as Float32Array;
    const color_attr        = this.geometry.getAttribute( "aColor" ).array as Float32Array;
    const scale_rot_attr    = this.geometry.getAttribute( "aScaleRot" ).array as Float32Array;

    const o4 = idx * 4;

    pos_start_attr[ o4 ]     = start_pos.x;
    pos_start_attr[ o4 + 1 ] = start_pos.y;
    pos_start_attr[ o4 + 2 ] = start_pos.z;
    pos_start_attr[ o4 + 3 ] = start_time;

    vel_duration_attr[ o4 ]     = velocity.x;
    vel_duration_attr[ o4 + 1 ] = velocity.y;
    vel_duration_attr[ o4 + 2 ] = velocity.z;
    vel_duration_attr[ o4 + 3 ] = duration;

    color_attr[ o4 ]     = color.r;
    color_attr[ o4 + 1 ] = color.g;
    color_attr[ o4 + 2 ] = color.b;
    color_attr[ o4 + 3 ] = alpha;

    scale_rot_attr[ o4 ]     = start_scale;
    scale_rot_attr[ o4 + 1 ] = end_scale;
    scale_rot_attr[ o4 + 2 ] = initial_angle;
    scale_rot_attr[ o4 + 3 ] = rotation_speed;

    this.geometry.getAttribute( "aPosStartTime" ).needsUpdate = true;
    this.geometry.getAttribute( "aVelDuration" ).needsUpdate  = true;
    this.geometry.getAttribute( "aColor" ).needsUpdate        = true;
    this.geometry.getAttribute( "aScaleRot" ).needsUpdate     = true;

    return this.getStableId( idx );
  }

  protected override onUpdate( _dt: number, now: number ): void
  {
    const mat = this.material as THREE.ShaderMaterial;
    if ( mat.uniforms.uTime )
    {
      mat.uniforms.uTime.value = now;
    }
  }

  protected override updateInstance( _progress: number, _now: number, _idx: number ): boolean
  {
    return true;
  }
}
